import { MAINTAINER_MCP_TOOLS, MCP_TOOLS, NEW_MEMBER_MCP_TOOLS, type McpTool } from '@infras/shared-types';
import { McpConnectSteps } from '../components/McpConnectSteps';
import { Icon } from '../components/Icon';

/**
 * MCP tools reference — rendered from the shared MCP_TOOLS catalog (the same one
 * the MCP server registers), so it can't drift. Inventory tools are read-only
 * (secrets names-only). Writes carry a "writes" marker, and the ones gated to
 * root|maintainer (MAINTAINER_MCP_TOOLS — hidden from an engineer's tools/list)
 * carry a role marker. BOTH badges come from the shared catalog, never a hand
 * list: a tool added to the gate must not need a docs edit to say so.
 */
const CATEGORIES: { key: McpTool['category']; label: string; icon: string }[] = [
  { key: 'search', label: 'Search', icon: 'search' },
  { key: 'repos', label: 'Repos', icon: 'repo' },
  { key: 'clusters', label: 'Clusters', icon: 'cluster' },
  { key: 'secrets', label: 'Secrets', icon: 'key' },
  { key: 'scheduler', label: 'Scheduler', icon: '⏱️' },
  { key: 'catalog', label: 'Catalog', icon: 'docs' },
  { key: 'access', label: 'Access requests', icon: 'access' },
];

export function McpDocsPage() {
  return (
    <div className="page">
      <div className="detail-head">
        <h1>MCP tools</h1>
        <span className="badge quiet">{MCP_TOOLS.length} tools</span>
        <span className="badge quiet">inventory read-only</span>
      </div>
      <div className="mcp-docs-intro">
        <p className="detail-desc">
          Hosted MCP over the same <code>infras</code> DB this dashboard uses. Inventory tools are
          read-only; secrets return <strong>names only</strong>, scoped by role (root = L0+L1+L3 ·
          maintainer = L1+L3 · engineer = L3). Access-request tools can file, list, read, and comment
          — approve/deny stays human here.
        </p>
        <p className="detail-desc">
          The <span className="badge quiet">writes</span> tools mutate data. Those also marked{' '}
          <span className="badge quiet">root|maintainer</span> are <strong>hidden</strong> from a
          lower role&rsquo;s tool list, not just refused when called. The MCP surface is deliberately
          narrower than this dashboard, never wider — so a few powers exist here and nowhere else:
          approving an agent command, deciding an access request, cancelling someone else&rsquo;s
          request, and assigning a repo Maintainer as that repo&rsquo;s own Owner.
        </p>
        <p className="detail-desc">
          New members get the onboarding + access-request tools until a root assigns their role:
        </p>
        <p className="mcp-tool-list">{NEW_MEMBER_MCP_TOOLS.join(' · ')}</p>
        <p className="detail-desc">Connect with the URL + token below.</p>
      </div>

      <h3 className="section">Connect your AI client</h3>
      <p className="detail-desc">
        Hosted Streamable HTTP — one entrypoint, no local install. Claude Desktop / Claude Code:
      </p>
      <McpConnectSteps />

      {CATEGORIES.map((cat) => {
        const tools = MCP_TOOLS.filter((t) => t.category === cat.key);
        if (!tools.length) return null;
        return (
          <div key={cat.key}>
            <h3 className="section">
              {cat.icon} {cat.label}
            </h3>
            {tools.map((t) => (
              <ToolCard key={t.name} tool={t} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function ToolCard({ tool }: { tool: McpTool }) {
  const params = Object.entries(tool.inputSchema.properties);
  const required = new Set(tool.inputSchema.required ?? []);
  return (
    <div className="dep-card">
      <div className="dep-head">
        <strong>{tool.name}</strong>
        <span className="muted">{tool.title}</span>
        {tool.writes && (
          <span className="badge" title="this tool mutates data">
            writes
          </span>
        )}
        {MAINTAINER_MCP_TOOLS.includes(tool.name) && (
          <span className="badge quiet" title="root or maintainer only — hidden from a lower role's tools/list">
            root|maintainer
          </span>
        )}
        <code className="tag-badge dep-example">{tool.example}</code>
      </div>
      <p className="dep-desc">{tool.description}</p>
      {params.length === 0 ? (
        <p className="docs-note">No parameters.</p>
      ) : (
        <div className="docs-table-scroll" role="region" aria-label="Tool parameters" tabIndex={0}><table>
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Required</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {params.map(([name, p]) => (
              <tr key={name}>
                <td className="text-xs">{name}</td>
                <td className="muted text-sm">{p.type}</td>
                <td>{required.has(name) ? <span className="mtx-yes">yes</span> : <span className="muted text-sm">no</span>}</td>
                <td className="muted text-sm">{p.description}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}
